/**
 * Versuchsseite „Nummer lesen“ (ADR 0033).
 *
 * Bevor die Texterkennung in den Scanner kommt, soll sie auf echten Handys
 * an echten Aufklebern gemessen werden: Wie oft trifft sie, wie lange
 * braucht sie, liest sie je etwas Falsches? Diese Seite zeigt genau das und
 * **bucht nichts**. Ein Treffer im Bestand fuehrt nur zur Geraeteseite oder
 * zum Etikett.
 *
 * Erreichbar unter `/versuch/texterkennung`, verlinkt unter „Konto“.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { Artikel } from "@thw/contracts";
import { useBestand } from "../../lokal/bestand.js";
import { rueckmeldungGeben } from "../../scan/Scanner.jsx";
import {
  LESEN_VOREINSTELLUNG,
  type LeseOptionen,
  type Leseergebnis,
} from "../../scan/texterkennung/erkenner.js";
import { artikelMitNummer, Bestaetigung } from "../../scan/texterkennung/nummern.js";
import { texterkennung, type Bereit, type Ladestand } from "../../scan/texterkennung/dienst.js";
import { etikettenAdresse } from "../../ui/EtikettHinweis.jsx";
import { Karte, Knopf, Nummer, Rueckmeldung, Schalter } from "../../ui/bausteine.jsx";

/** Breite des Lesestreifens als Anteil am Bild. */
const STREIFEN_ANTEIL = 0.9;
/** Hoehe des Streifens als Anteil seiner Breite -- flach, weil Nummern Zeilen sind. */
const STREIFEN_SEITEN = 0.45;
/** Laengste Seite eines gewaehlten Fotos. */
const FOTO_HOECHSTENS = 1920;
const ZOOM_START = 2;

interface Faehigkeiten {
  zoom?: { min: number; max: number };
  torch?: boolean;
}

interface Statistik {
  versuche: number;
  mitNummer: number;
  imBestand: number;
  nichtImBestand: number;
  bestaetigt: number;
  zeiten: number[];
}

const LEERE_STATISTIK: Statistik = {
  versuche: 0,
  mitNummer: 0,
  imBestand: 0,
  nichtImBestand: 0,
  bestaetigt: 0,
  zeiten: [],
};

function median(werte: readonly number[]): number {
  if (werte.length === 0) return 0;
  const s = [...werte].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? (s[m] ?? 0) : ((s[m - 1] ?? 0) + (s[m] ?? 0)) / 2;
}

const mb = (bytes: number): string => `${(bytes / 1e6).toFixed(1).replace(".", ",")} MB`;
const sek = (ms: number): string => `${(ms / 1000).toFixed(2).replace(".", ",")} s`;
const prozent = (anteil: number): string => `${String(anteil * 100)}%`;

/** Der Lesestreifen in Videopixeln. */
function streifen(breite: number, hoehe: number): { x: number; y: number; b: number; h: number } {
  const b = Math.round(breite * STREIFEN_ANTEIL);
  const h = Math.min(hoehe, Math.round(b * STREIFEN_SEITEN));
  return { x: Math.round((breite - b) / 2), y: Math.round((hoehe - h) / 2), b, h };
}

/** Zeichnet die gefundenen Zeilen ins Bild: gruen mit Nummer, sonst gelb. */
function zeilenEinzeichnen(leinwand: HTMLCanvasElement, ergebnis: Leseergebnis): void {
  const ctx = leinwand.getContext("2d");
  if (ctx === null) return;
  const mitte = [leinwand.width / 2, leinwand.height / 2] as const;
  ctx.lineWidth = Math.max(2, leinwand.width / 300);
  for (const z of ergebnis.zeilen) {
    // Die Ecken beziehen sich auf das fuer den Versuch gedrehte Bild; fuer
    // die Anzeige ungefaehr zurueckdrehen genuegt.
    const r = (-z.drehung * Math.PI) / 180;
    ctx.strokeStyle = /\d{4}\D?\d{6}/.test(z.text) ? "#16a34a" : "#eab308";
    ctx.beginPath();
    z.ecken.forEach(([x, y], i) => {
      const dx = x - mitte[0];
      const dy = y - mitte[1];
      const px = mitte[0] + dx * Math.cos(r) - dy * Math.sin(r);
      const py = mitte[1] + dx * Math.sin(r) + dy * Math.cos(r);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.closePath();
    ctx.stroke();
  }
}

export function Texterkennung() {
  const bestand = useBestand();
  const videoRef = useRef<HTMLVideoElement>(null);
  const anzeigeRef = useRef<HTMLCanvasElement>(null);
  const spurRef = useRef<MediaStreamTrack | null>(null);
  const dauerndRef = useRef(false);
  const beschaeftigtRef = useRef(false);
  const bestaetigungRef = useRef(new Bestaetigung());

  const [ladestand, setLadestand] = useState<Ladestand | null>(null);
  const [bereit, setBereit] = useState<Bereit | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kameraAn, setKameraAn] = useState(false);
  const [videoMasse, setVideoMasse] = useState<{ b: number; h: number } | null>(null);
  const [faehig, setFaehig] = useState<Faehigkeiten>({});
  const [lampe, setLampe] = useState(false);
  const [dauernd, setDauernd] = useState(false);
  const [optionen, setOptionen] = useState<LeseOptionen>(LESEN_VOREINSTELLUNG);
  const [ergebnis, setErgebnis] = useState<Leseergebnis | null>(null);
  const [treffer, setTreffer] = useState<Artikel | null>(null);
  const [bestaetigt, setBestaetigt] = useState<Artikel | null>(null);
  const [statistik, setStatistik] = useState<Statistik>(LEERE_STATISTIK);
  const [kopiert, setKopiert] = useState(false);

  // --- Modelle laden, sobald die Seite offen ist ---
  useEffect(() => {
    const dienst = texterkennung();
    const abmelden = dienst.beobachten(setLadestand);
    dienst.bereit().then(setBereit, (f: unknown) => {
      setFehler(`Texterkennung ließ sich nicht laden: ${String(f)}`);
    });
    return abmelden;
  }, []);

  // --- Kamera ---
  const kameraStoppen = useCallback(() => {
    dauerndRef.current = false;
    setDauernd(false);
    const strom = videoRef.current?.srcObject as MediaStream | null | undefined;
    strom?.getTracks().forEach((t) => {
      t.stop();
    });
    if (videoRef.current !== null) videoRef.current.srcObject = null;
    spurRef.current = null;
    setKameraAn(false);
    setLampe(false);
    setVideoMasse(null);
  }, []);

  useEffect(() => kameraStoppen, [kameraStoppen]);

  const kameraStarten = useCallback(async () => {
    setFehler(null);
    try {
      const strom = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      const video = videoRef.current;
      if (video === null) return;
      video.srcObject = strom;
      await video.play();
      const spur = strom.getVideoTracks()[0] ?? null;
      spurRef.current = spur;
      let kann: Faehigkeiten = {};
      try {
        kann = (spur?.getCapabilities() ?? {}) as Faehigkeiten;
      } catch {
        // Firefox und Safari kennen getCapabilities teils nicht.
      }
      setFaehig(kann);
      if (kann.zoom !== undefined && spur !== null) {
        const wert = Math.min(kann.zoom.max, Math.max(kann.zoom.min, ZOOM_START));
        await spur
          .applyConstraints({ advanced: [{ zoom: wert } as MediaTrackConstraintSet] })
          .catch(() => undefined);
      }
      setVideoMasse({ b: video.videoWidth, h: video.videoHeight });
      setKameraAn(true);
    } catch (f) {
      setFehler(`Kamera ließ sich nicht starten: ${String(f)}`);
    }
  }, []);

  const lampeUmschalten = useCallback(async () => {
    const spur = spurRef.current;
    if (spur === null) return;
    const neu = !lampe;
    await spur
      .applyConstraints({ advanced: [{ torch: neu } as MediaTrackConstraintSet] })
      .catch(() => undefined);
    setLampe(neu);
  }, [lampe]);

  // --- Lesen ---
  const auswerten = useCallback(
    async (bild: ImageData, laufend: boolean) => {
      const anzeige = anzeigeRef.current;
      if (anzeige !== null) {
        anzeige.width = bild.width;
        anzeige.height = bild.height;
        anzeige.getContext("2d")?.putImageData(bild, 0, 0);
      }
      const e = await texterkennung().lesen(bild, optionen);
      if (anzeige !== null) zeilenEinzeichnen(anzeige, e);

      const artikel = bestand === undefined ? [] : [...bestand.artikel.values()];
      const imBestand =
        e.nummern.map((n) => artikelMitNummer(artikel, n)).find((a) => a !== null) ?? null;
      const steht = laufend
        ? bestaetigungRef.current.melden(imBestand?.inventarnummer ?? null)
        : imBestand !== null;
      if (steht && imBestand !== null) {
        setBestaetigt(imBestand);
        rueckmeldungGeben(true);
      }
      setErgebnis(e);
      setTreffer(imBestand);
      setStatistik((s) => ({
        versuche: s.versuche + 1,
        mitNummer: s.mitNummer + (e.nummern.length > 0 ? 1 : 0),
        imBestand: s.imBestand + (imBestand !== null ? 1 : 0),
        nichtImBestand: s.nichtImBestand + (e.nummern.length > 0 && imBestand === null ? 1 : 0),
        bestaetigt: s.bestaetigt + (steht && imBestand !== null ? 1 : 0),
        zeiten: [...s.zeiten, e.ms.gesamt],
      }));
    },
    [bestand, optionen],
  );

  const bildAusKamera = useCallback((): ImageData | null => {
    const video = videoRef.current;
    if (video === null || video.videoWidth === 0) return null;
    const s = streifen(video.videoWidth, video.videoHeight);
    const leinwand = document.createElement("canvas");
    leinwand.width = s.b;
    leinwand.height = s.h;
    const ctx = leinwand.getContext("2d", { willReadFrequently: true });
    if (ctx === null) return null;
    ctx.drawImage(video, s.x, s.y, s.b, s.h, 0, 0, s.b, s.h);
    return ctx.getImageData(0, 0, s.b, s.h);
  }, []);

  // Die Dauerschleife soll geaenderte Optionen sofort nutzen, nicht die vom Start.
  const auswertenRef = useRef(auswerten);
  useEffect(() => {
    auswertenRef.current = auswerten;
  }, [auswerten]);

  const einmalLesen = useCallback(
    async (laufend: boolean) => {
      if (beschaeftigtRef.current) return;
      const bild = bildAusKamera();
      if (bild === null) return;
      beschaeftigtRef.current = true;
      try {
        await auswertenRef.current(bild, laufend);
      } catch (f) {
        setFehler(String(f));
        dauerndRef.current = false;
        setDauernd(false);
      } finally {
        beschaeftigtRef.current = false;
      }
    },
    [bildAusKamera],
  );

  const dauerndUmschalten = useCallback(
    async (an: boolean) => {
      dauerndRef.current = an;
      setDauernd(an);
      bestaetigungRef.current = new Bestaetigung();
      while (dauerndRef.current) {
        await einmalLesen(true);
        // Kurz Luft lassen, damit die Anzeige nachkommt.
        await new Promise((r) => setTimeout(r, 50));
      }
    },
    [einmalLesen],
  );

  const fotoGewaehlt = useCallback(
    async (datei: File | undefined) => {
      if (datei === undefined) return;
      setFehler(null);
      try {
        const bitmap = await createImageBitmap(datei);
        const f = Math.min(1, FOTO_HOECHSTENS / Math.max(bitmap.width, bitmap.height));
        const leinwand = document.createElement("canvas");
        leinwand.width = Math.round(bitmap.width * f);
        leinwand.height = Math.round(bitmap.height * f);
        const ctx = leinwand.getContext("2d");
        if (ctx === null) return;
        ctx.drawImage(bitmap, 0, 0, leinwand.width, leinwand.height);
        await auswerten(ctx.getImageData(0, 0, leinwand.width, leinwand.height), false);
      } catch (f) {
        setFehler(`Foto ließ sich nicht lesen: ${String(f)}`);
      }
    },
    [auswerten],
  );

  const ergebnisKopieren = useCallback(async () => {
    const zeilen = [
      `Versuch Nummer lesen, ${new Date().toLocaleString("de-DE")}`,
      `Gerät: ${navigator.userAgent}`,
      `Threads: ${String(bereit?.threads ?? "?")}, Laden: ${bereit === null ? "?" : sek(bereit.ms)} (${bereit === null ? "?" : mb(bereit.bytes)})`,
      `Kamerabild: ${videoMasse === null ? "-" : `${String(videoMasse.b)}x${String(videoMasse.h)}`}`,
      `Optionen: Kontrast nachfassen ${optionen.kontrastNachfassen ? "ja" : "nein"}, schräg nachfassen ${optionen.schraegNachfassen ? "ja" : "nein"}`,
      `Versuche: ${String(statistik.versuche)}, mit Nummer: ${String(statistik.mitNummer)}, im Bestand: ${String(statistik.imBestand)}, nicht im Bestand: ${String(statistik.nichtImBestand)}, bestätigt: ${String(statistik.bestaetigt)}`,
      `Dauer je Versuch: Median ${sek(median(statistik.zeiten))}, längster ${sek(Math.max(0, ...statistik.zeiten))}`,
    ];
    try {
      await navigator.clipboard.writeText(zeilen.join("\n"));
      setKopiert(true);
      setTimeout(() => {
        setKopiert(false);
      }, 2000);
    } catch {
      setFehler("Kopieren ging nicht. Bitte die Zahlen abschreiben.");
    }
  }, [bereit, optionen, statistik, videoMasse]);

  const s = videoMasse === null ? null : streifen(videoMasse.b, videoMasse.h);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Versuch: Nummer lesen</h1>
        <p className="text-neutral-600 dark:text-neutral-300">
          Liest Inventarnummern von Aufklebern ohne Barcode. Hier wird nur gemessen und nichts
          gebucht.
        </p>
      </header>

      {fehler !== null && <Rueckmeldung art="warnung" kopf="Fehler" zusatz={fehler} />}

      {bereit === null ? (
        <Rueckmeldung
          art="still"
          kopf="Texterkennung wird geladen …"
          zusatz={
            ladestand === null
              ? "Einmalig rund 30 MB, danach aus dem Zwischenspeicher."
              : `${mb(ladestand.geladen)}${ladestand.gesamt === null ? "" : ` von ${mb(ladestand.gesamt)}`}`
          }
        />
      ) : (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Bereit nach {sek(bereit.ms)} · {mb(bereit.bytes)} · {bereit.threads}{" "}
          {bereit.threads === 1 ? "Thread" : "Threads"}
        </p>
      )}

      <div className="relative overflow-hidden rounded-xl bg-black">
        <video ref={videoRef} className="block w-full" playsInline muted />
        {s !== null && videoMasse !== null && (
          <div
            className="pointer-events-none absolute rounded-md border-4 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
            style={{
              left: prozent(s.x / videoMasse.b),
              top: prozent(s.y / videoMasse.h),
              width: prozent(s.b / videoMasse.b),
              height: prozent(s.h / videoMasse.h),
            }}
          />
        )}
        {!kameraAn && <p className="p-8 text-center text-white">Kamera aus</p>}
      </div>
      {kameraAn && (
        <p className="-mt-2 text-center text-sm font-semibold">
          Nummer waagerecht in den Rahmen, nah heran
        </p>
      )}

      <div className="grid grid-cols-2 gap-2">
        {kameraAn ? (
          <Knopf onClick={kameraStoppen}>Kamera stoppen</Knopf>
        ) : (
          <Knopf art="haupt" onClick={() => void kameraStarten()}>
            Kamera starten
          </Knopf>
        )}
        <Knopf
          art={kameraAn ? "haupt" : "neben"}
          disabled={!kameraAn || bereit === null || dauernd}
          onClick={() => void einmalLesen(false)}
        >
          Einmal lesen
        </Knopf>
      </div>
      <Schalter
        beschriftung="Dauernd lesen"
        hinweis="Liest Bild für Bild; eine Nummer gilt, wenn sie zweimal hintereinander gleich gelesen wurde."
        an={dauernd}
        gesperrt={!kameraAn || bereit === null}
        aendern={(an) => void dauerndUmschalten(an)}
      />
      {faehig.torch === true && (
        <Schalter beschriftung="Lampe" an={lampe} aendern={() => void lampeUmschalten()} />
      )}

      {bestaetigt !== null && (
        <Karte titel="Erkannt">
          <p className="text-xl font-bold">{bestaetigt.bezeichnung}</p>
          <Nummer>{bestaetigt.inventarnummer}</Nummer>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/artikel/${bestaetigt.id}`}
              className="flex min-h-12 items-center justify-center rounded-xl bg-blue-700 px-4 font-semibold text-white"
            >
              Gerät öffnen
            </Link>
            {bestand !== undefined && (
              <Link
                to={etikettenAdresse(bestand, [bestaetigt.id])}
                className="flex min-h-12 items-center justify-center rounded-xl bg-neutral-200 px-4 font-semibold dark:bg-neutral-700"
              >
                Etikett drucken
              </Link>
            )}
          </div>
        </Karte>
      )}

      <Karte titel="Letzter Versuch">
        <canvas ref={anzeigeRef} className="w-full rounded-lg bg-neutral-100 dark:bg-neutral-900" />
        {ergebnis === null ? (
          <p className="text-neutral-500">Noch nichts gelesen.</p>
        ) : (
          <>
            <p className="font-semibold">
              {treffer !== null
                ? `Im Bestand: ${treffer.bezeichnung}`
                : ergebnis.nummern.length > 0
                  ? `Nummer gelesen, aber nicht im Bestand: ${ergebnis.nummern.join(", ")}`
                  : "Keine Inventarnummer gefunden"}
            </p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {sek(ergebnis.ms.gesamt)} (Suche {sek(ergebnis.ms.suche)}, Lesen{" "}
              {sek(ergebnis.ms.lesen)}
              {ergebnis.durchgaenge > 1 ? `, ${String(ergebnis.durchgaenge)} Durchgänge` : ""})
            </p>
            <ul className="flex flex-col gap-1 text-sm">
              {ergebnis.zeilen.map((z, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className="font-mono break-all">{z.text}</span>
                  <span className="shrink-0 text-neutral-500">
                    {Math.round(z.sicherheit * 100)} %
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        <label className="flex min-h-12 cursor-pointer items-center justify-center rounded-xl bg-neutral-200 px-4 font-semibold dark:bg-neutral-700">
          Foto wählen
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={bereit === null}
            onChange={(e) => {
              void fotoGewaehlt(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
      </Karte>

      <Karte titel="Messung">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt>Versuche</dt>
          <dd className="text-right">{statistik.versuche}</dd>
          <dt>mit Nummer</dt>
          <dd className="text-right">{statistik.mitNummer}</dd>
          <dt>davon im Bestand</dt>
          <dd className="text-right">{statistik.imBestand}</dd>
          <dt>nicht im Bestand</dt>
          <dd className="text-right">{statistik.nichtImBestand}</dd>
          <dt>bestätigt (zweimal gleich)</dt>
          <dd className="text-right">{statistik.bestaetigt}</dd>
          <dt>Dauer, Median</dt>
          <dd className="text-right">{sek(median(statistik.zeiten))}</dd>
          <dt>Dauer, längster</dt>
          <dd className="text-right">{sek(Math.max(0, ...statistik.zeiten))}</dd>
        </dl>
        <Schalter
          beschriftung="Kontrast nachfassen"
          hinweis="Findet der erste Durchgang nichts, noch einmal mit starkem Kontrast."
          an={optionen.kontrastNachfassen}
          aendern={(kontrastNachfassen) => {
            setOptionen((o) => ({ ...o, kontrastNachfassen }));
          }}
        />
        <Schalter
          beschriftung="Schräg nachfassen"
          hinweis="Dann noch um ±25° gedreht. Kostet Zeit."
          an={optionen.schraegNachfassen}
          aendern={(schraegNachfassen) => {
            setOptionen((o) => ({ ...o, schraegNachfassen }));
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          <Knopf onClick={() => void ergebnisKopieren()}>
            {kopiert ? "Kopiert" : "Ergebnis kopieren"}
          </Knopf>
          <Knopf
            onClick={() => {
              setStatistik(LEERE_STATISTIK);
              setBestaetigt(null);
            }}
          >
            Zurücksetzen
          </Knopf>
        </div>
      </Karte>
    </div>
  );
}
